"use client";

import * as React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Paper,
} from "@mui/material";

type Place = {
  name: string;
  rating: number | null;
  user_ratings_total: number | null;
  vicinity: string | null;
  place_id: string | null;
  lat: number | null;
  lng: number | null;
};

const TYPES = [
  { key: "restaurant", labelKey: "nearby_restaurants" },
  { key: "cafe", labelKey: "nearby_cafes" },
  { key: "supermarket", labelKey: "nearby_supermarkets" },
  { key: "pharmacy", labelKey: "nearby_pharmacies" },
  { key: "tourist_attraction", labelKey: "nearby_attractions" },
  { key: "bar", labelKey: "nearby_beaches" }, // (beach_bar δεν είναι official type στο Places)
] as const;

function toRad(x: number) {
  return (x * Math.PI) / 180;
}
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function fmtKm(km: number) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
}

export default function NearbyPlaces({
  lat,
  lng,
  t,
}: {
  lat: number;
  lng: number;
  t: (key: string, vars?: Record<string, any>) => string;
}) {
  const [type, setType] =
    React.useState<(typeof TYPES)[number]["key"]>("restaurant");
  const [items, setItems] = React.useState<Place[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const mapsPlaceUrl = (
    placeId?: string | null,
    name?: string | null,
    vicinity?: string | null
  ) => {
    const q = `${name || ""}${vicinity ? " " + vicinity : ""}`.trim();
    if (placeId) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        q
      )}&query_place_id=${encodeURIComponent(placeId)}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      q
    )}`;
  };

  const directionsUrl = (
    placeId?: string | null,
    name?: string | null,
    vicinity?: string | null,
    destLat?: number | null,
    destLng?: number | null
  ) => {
    const origin = `${lat},${lng}`;
    const destText = `${name || ""}${vicinity ? " " + vicinity : ""}`.trim();

    if (placeId) {
      return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
        origin
      )}&destination=${encodeURIComponent(
        destText
      )}&destination_place_id=${encodeURIComponent(
        placeId
      )}&travelmode=driving`;
    }

    if (destLat != null && destLng != null) {
      return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
        origin
      )}&destination=${destLat},${destLng}&travelmode=driving`;
    }

    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin
    )}&destination=${encodeURIComponent(destText)}&travelmode=driving`;
  };

  const load = React.useCallback(async () => {
    setErr(null);

    // @ts-ignore
    const g = (window as any).google;
    if (!g?.maps?.places) {
      setErr("Google Places library not loaded");
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const center = new g.maps.LatLng(lat, lng);

      // PlacesService θέλει "map" ή "div". Φτιάχνουμε hidden div.
      const div = document.createElement("div");
      const service = new g.maps.places.PlacesService(div);

      const req = {
        location: center,
        radius: 2500,
        type, // restaurant/cafe/etc
      };

      const results: Place[] = await new Promise((resolve, reject) => {
        service.nearbySearch(req, (res: any[], status: string) => {
          if (status === g.maps.places.PlacesServiceStatus.OK || status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            const out: Place[] = (res || []).map((p: any) => {
              const loc = p?.geometry?.location;
              const plat = loc?.lat ? loc.lat() : null;
              const plng = loc?.lng ? loc.lng() : null;

              return {
                name: p?.name ?? null,
                rating: p?.rating ?? null,
                user_ratings_total: p?.user_ratings_total ?? null,
                vicinity: p?.vicinity ?? null,
                place_id: p?.place_id ?? null,
                lat: plat,
                lng: plng,
              };
            });
            resolve(out);
          } else {
            reject(new Error(status));
          }
        });
      });

      setItems(results);
    } catch (e: any) {
      setItems([]);
      setErr(e?.message || "places error");
    } finally {
      setLoading(false);
    }
  }, [lat, lng, type]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
        {t("nearby_title")}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.4 }}
      >
        {TYPES.map((x) => (
          <Chip
            key={x.key}
            label={t(x.labelKey)}
            onClick={() => setType(x.key)}
            color={type === x.key ? "primary" : "default"}
            variant={type === x.key ? "filled" : "outlined"}
            sx={{ borderRadius: 999, fontWeight: 700 }}
          />
        ))}
      </Stack>

      <Divider sx={{ mb: 1.4 }} />

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography color="text.secondary">{t("loading")}</Typography>
        </Stack>
      ) : err ? (
        <Typography color="error">{err}</Typography>
      ) : items.length ? (
        <Stack spacing={1.1}>
          {items.map((p) => {
            const href = mapsPlaceUrl(p.place_id, p.name, p.vicinity);
            const dir = directionsUrl(p.place_id, p.name, p.vicinity, p.lat, p.lng);

            const dist =
              p.lat != null && p.lng != null
                ? fmtKm(haversineKm(lat, lng, p.lat, p.lng))
                : null;

            return (
              <Paper
                key={p.place_id ?? `${p.name}-${p.vicinity}`}
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.25 },
                  borderRadius: 2.5,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
              >
                <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {p.name}
                </Typography>

                {p.vicinity ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.3 }}
                  >
                    {p.vicinity}
                  </Typography>
                ) : null}

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.6 }}>
                  {p.rating != null ? (
                    <Typography variant="caption" color="text.secondary">
                      ★ {p.rating}
                      {p.user_ratings_total ? ` (${p.user_ratings_total})` : ""}
                    </Typography>
                  ) : null}

                  {dist ? (
                    <Typography variant="caption" color="text.secondary">
                      • {dist}
                    </Typography>
                  ) : null}

                  <Box sx={{ flex: 1 }} />

                  {dir ? (
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, textDecoration: "underline" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(dir, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {t("directions")}
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <Typography color="text.secondary">{t("nearby_empty")}</Typography>
      )}
    </Box>
  );
}
